<template>
    <div>
        <template v-if="loading">
            <i class="fas fa-cog fa-spin"></i> Loading...
        </template>
        <template v-else>
            <vue-headful :title="'[Custom Report] - AMOS365'" />

            <nav aria-label="breadcrumb" class="d-print-none">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/reports">My Saved Reports</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page" v-if="!report.id">
                        Custom Report Builder
                    </li>
                    <template v-else>
                        <li class="breadcrumb-item">
                            <router-link :to="`/reports/view/${report.id}`">{{ report.report_name }}</router-link>
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">
                            {{ can_edit ? 'Edit' : 'Generate' }}
                        </li>
                    </template>

                </ol>
            </nav>

            <h4 class="page-header mb-4">
                <span class="d-print-none">Custom Report Builder</span> <span v-if="report.id" class="text-muted"><span class="d-print-none">/ </span>{{ report.report_name }}</span>
                <span v-if="report.id && hasUnsavedChanges" class="badge badge-warning align-middle ml-2 d-print-none" title="You have unsaved changes to this report's fields. Save the report to keep them.">
                    <i class="fa fa-exclamation-triangle"></i> Unsaved changes
                </span>
            </h4>

            <div class="row mb-4 d-print-none" v-if="can_edit">
                <div class="col-md-8 col-12">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Display Fields ({{ this.selected_display_fields.length }}):</strong>

                            <div class="actions pull-right">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="show_display_fields = !show_display_fields" :title="(show_display_fields == true ? 'Hide' : 'Show')+' Display Fields'">
                                    <i :class="'fa fa-caret-'+(show_display_fields === true ? 'up' : 'down')"></i>
                                </button>
                            </div>
                        </div>
                        <div class="card-body" v-show="show_display_fields">
                            <div v-for="(field, i) in selected_display_fields" :key="i" class="form-inline mb-2">
                                <div class="form-group mr-2" v-if="i > 0">
                                    <button type="button" class="btn btn-xs btn-default" v-on:click="removeDisplayField(i)"><i class="fa fa-window-close text-danger"></i></button>
                                </div>
                                <div class="form-group">
                                    <b-form-select v-model="selected_display_fields[i].key" :options="displayFieldOptions" size="sm" :class="{'is-invalid': errors['display_fields.'+i+'.key']}" />

                                    <div v-if="errors['display_fields.'+i+'.key']" class="col-12 mt-1 text-danger">
                                        <span v-for="err in errors['display_fields.'+i+'.key']">{{ err }}</span>
                                    </div>
                                </div>

                                <div class="form-group ml-2" v-if="selected_display_fields[i].key !== null && hasProduct(selected_display_fields[i].key)" style="max-width: 250px;">
                                    <b-form-select v-model="selected_display_fields[i].value" :options="productOptions" size="sm" :class="{'is-invalid': errors['display_fields.'+i+'.value']}" />

                                    <div v-if="errors['display_fields.'+i+'.value']" class="col-12 mt-1 text-danger">
                                        <span v-for="err in errors['display_fields.'+i+'.value']">{{ err }}</span>
                                    </div>
                                </div>

                                <div class="form-group ml-2" v-if="selected_display_fields[i].key !== null && hasAddressType(selected_display_fields[i].key)">
                                    <b-form-select v-model="selected_display_fields[i].type" :options="addressTypes" size="sm" :class="{'is-invalid': errors['display_fields.'+i+'.type']}" />

                                    <div v-if="errors['display_fields.'+i+'.type']" class="col-12 mt-1 text-danger">
                                        <span v-for="err in errors['display_fields.'+i+'.type']">{{ err }}</span>
                                    </div>
                                </div>

                                <div class="form-group ml-2" v-if="selected_display_fields[i].key !== null && selected_display_fields[i].key === 'record_phones.phone'">
                                    <b-form-select v-model="selected_display_fields[i].type" :options="phoneTypes" size="sm" :class="{'is-invalid': errors['display_fields.'+i+'.type']}" />

                                    <div v-if="errors['display_fields.'+i+'.type']" class="col-12 mt-1 text-danger">
                                        <span v-for="err in errors['display_fields.'+i+'.type']">{{ err }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-4">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="addDisplayField()">
                                    <span class="text-success"><i class="fa fa-plus-circle"></i> Add</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-4 mb-md-0">
                        <div class="card-header">
                            <strong>Search Criteria ({{ this.selected_search_fields.length }}):</strong>
                        </div>
                        <div class="card-body report-search">
                            <div v-for="(field, i) in selected_search_fields" :key="i" class="form-inline mb-2">
                                <div class="form-group mr-3">
                                    <button type="button" class="btn btn-xs btn-default" v-on:click="removeSearchField(i)"><i class="fa fa-window-close text-danger"></i></button>
                                </div>
                                <div class="form-group pt-2 mr-3 mb-3" v-if="i > 0">
                                    &nbsp;AND
                                </div>
                                <div class="form-group mr-3">
                                    <b-form-select v-model="selected_search_fields[i].key" :options="searchFieldOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.key']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key !== null && hasAddressType(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].type" :options="addressTypes" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.type']}" />

                                    <div v-if="errors['search_fields.'+i+'.type']" class="col-12 mt-1 text-danger">
                                        <span v-for="err in errors['search_fields.'+i+'.type']">{{ err }}</span>
                                    </div>
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key !== null && hasLogic(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].logic" :options="logicOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.key']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key !== null && hasAgent(selected_search_fields[i].key)">
                                    <v-select
                                        v-model="selected_search_fields[i].value"
                                        :options="manager_agents"
                                        :reduce="agent => agent.id"
                                        label="agent_name"
                                        :class="{'is-invalid': errors.manager_id_agent}"
                                        placeholder="-- Select an Agent --"
                                        @search="searchAgents"
                                        :filterable="false"
                                        style="min-width: 200px;" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key === 'agents.gender'">
                                    <b-form-radio-group
                                        v-model="selected_search_fields[i].value"
                                        :options="[{value: 'Male', text: 'Male'}, {value: 'Female', text: 'Female'}]"
                                        name="gender"
                                        plain
                                        :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>
                                <div class="form-group mr-3" v-if="hasOkayToContact(selected_search_fields[i].key)">
                                    <b-form-radio-group v-model="selected_search_fields[i].value" name="okay_to_contact" plain :class="{'is-invalid': errors['search_fields.'+i+'.value']}">
                                        <b-form-radio :value="0">Yes</b-form-radio>
                                        <b-form-radio :value="1">No</b-form-radio>
                                    </b-form-radio-group>
                                </div>
                                <div class="form-group mr-3" v-if="hasActive(selected_search_fields[i].key)">
                                    <b-form-radio-group v-model="selected_search_fields[i].value" name="is_active" plain :class="{'is-invalid': errors['search_fields.'+i+'.value']}">
                                        <b-form-radio :value="1">Active Only</b-form-radio>
                                        <b-form-radio :value="0">Inactive Only</b-form-radio>
                                    </b-form-radio-group>
                                </div>
                                <div class="form-group mr-3" v-if="selected_search_fields[i].key === 'agents.is_prospect'">
                                    <b-form-radio-group v-model="selected_search_fields[i].value" name="is_prospect" plain :class="{'is-invalid': errors['search_fields.'+i+'.value']}">
                                        <b-form-radio :value="0">Non-Prospective Only</b-form-radio>
                                        <b-form-radio :value="1">Prospective Only</b-form-radio>
                                    </b-form-radio-group>
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key !== null && hasDateLogic(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].logic" :options="dateLogicOptions" size="sm" />
                                </div>

                                <div class="form-group" v-if="selected_search_fields[i].key !== null && hasLogic(selected_search_fields[i].key)">
                                    <b-form-input v-model="selected_search_fields[i].value" size="sm" placeholder="Enter Search Value" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>
                                <div class="form-group input-group-sm" v-if="selected_search_fields[i].key !== null && hasDateLogic(selected_search_fields[i].key)">
                                    <input type="date" v-model="selected_search_fields[i].value" placeholder="Enter Date Value" :class="{'form-control': true, 'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group" v-if="selected_search_fields[i].key !== null && hasProduct(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="productOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />

                                    <div v-if="errors['search_fields.'+i+'.value']" class="text-danger mt-1 ml-1">
                                        <ul class="list-unstyled mb-0">
                                            <li v-for="err in errors['search_fields.'+i+'.value']">{{ err }}</li>
                                        </ul>
                                    </div>
                                </div>

                                <div v-if="selected_search_fields[i].key !== null && selected_search_fields[i].key === 'product_contracts.contract_status_id'" class="form-group">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="contractStatusOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />

                                    <div v-if="errors['search_fields.'+i+'.value']" class="text-danger mt-1 ml-1">
                                        <ul class="list-unstyled mb-0">
                                            <li v-for="err in errors['search_fields.'+i+'.value']">{{ err }}</li>
                                        </ul>
                                    </div>
                                </div>

                                <div v-if="selected_search_fields[i].key !== null && selected_search_fields[i].key === 'product_contract_statuses.contract_status_type'" class="form-group">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="contractStatusTypeOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />

                                    <div v-if="errors['search_fields.'+i+'.value']" class="text-danger mt-1 ml-1">
                                        <ul class="list-unstyled mb-0">
                                            <li v-for="err in errors['search_fields.'+i+'.value']">{{ err }}</li>
                                        </ul>
                                    </div>
                                </div>

                                <div class="form-group" v-if="selected_search_fields[i].key !== null && hasProductType(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="productTypeOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />

                                    <div v-if="errors['search_fields.'+i+'.value']" class="text-danger mt-1 ml-1">
                                        <ul class="list-unstyled mb-0">
                                            <li v-for="err in errors['search_fields.'+i+'.value']">{{ err }}</li>
                                        </ul>
                                    </div>
                                </div>

                                <div class="form-group" v-if="selected_search_fields[i].key !== null && hasUserLogic(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="userOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group" v-if="selected_search_fields[i].key === 'agents.notes' || selected_search_fields[i].key === 'agencies.notes'">
                                    includes&nbsp;<b-form-input v-model="selected_search_fields[i].value" size="sm" placeholder="Enter Notes Phrase" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key === 'agents.res_state_id' || selected_search_fields[i].key === 'agencies.resident_state_id' || selected_search_fields[i].key === 'record_addresses.state_id'">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="stateOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key !== null && hasAgency(selected_search_fields[i].key)">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="agencyOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key === 'do_not_contact_codes.dnc_code_reason'">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="doNotContactOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div class="form-group mr-3" v-if="selected_search_fields[i].key === 'agents.marketer_code_id' || selected_search_fields[i].key === 'agencies.marketer_code_id'">
                                    <b-form-select v-model="selected_search_fields[i].value" :options="marketerCodeOptions" size="sm" :class="{'is-invalid': errors['search_fields.'+i+'.value']}" />
                                </div>

                                <div v-if="errors['search_fields.'+i+'.value']" class="text-danger mt-1 ml-1">
                                    <ul class="list-unstyled mb-0">
                                        <li v-for="err in errors['search_fields.'+i+'.value']">{{ err }}</li>
                                    </ul>
                                </div>
                            </div>
                            <div class="mt-4">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="addSearchField()">
                                    <span class="text-success"><i class="fa fa-plus-circle"></i> Add</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 col-12">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Report Type:</strong>
                        </div>
                        <div class="card-body">
                            <b-form-select v-model="record_type" :options="reportTypeOptions" size="sm" :class="{'is-invalid': errors['record_type']}" v-if="!report.id" />
                            <span v-else>
                                {{ record_type }}
                            </span>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header">
                            <strong>Save Report:</strong>
                        </div>
                        <div class="card-body">
                            <div v-if="canEdit">
                                <b-form-input v-model="report.report_name" placeholder="Enter Report Name" :class="{'is-invalid': errors.report_name}" size="sm" />

                                <div class="form-group mt-2">
                                    <b-form-textarea v-model="report.description" placeholder="Enter a Report Description (Optional)" rows="4" />
                                </div>

                                <div class="form-group mt-2" v-if="$root.hasPermission('reports.manage.all')">
                                    <b-form-checkbox v-model="report.is_public" :value="1" :unchecked-value="0" plain>Public Report <i class="fa fa-question-circle" title="If set to Public, this report will be available to all users with the basic reporting view permissions"></i></b-form-checkbox>
                                </div>

                                <div class="form-group mt-2">
                                    <b-form-checkbox v-model="save_as_new" :value="true" :unchecked-value="false" v-if="report.id" plain>Save As New Report</b-form-checkbox>
                                </div>

                                <div class="form-group mt-2">
                                    <button type="text" class="btn btn-sm btn-default" v-on:click="saveReport()" :disabled="generating">
                                        <i class="fa fa-save"></i> Save Report
                                    </button>
                                </div>
                            </div>
                            <div v-else>
                                <div class="row mb-2">
                                    <div class="col-md-3">
                                        Report Name:
                                    </div>   
                                    <div class="col-md-9">
                                        {{ report.report_name }}
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3">
                                        Description:
                                    </div>   
                                    <div class="col-md-9">
                                        {{ report.description }}
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3">
                                        Public
                                    </div>   
                                    <div class="col-md-9">
                                        <label class="badge badge-info" v-if="report.is_public == 1">Public</label>
                                        <label class="badge badge-danger" v-else>Private</label>
                                    </div>
                                </div>
                            </div>                            
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mb-4" v-else>
                <div class="card-body">
                    {{ report.description }}
                </div>
            </div>

            <div class="mb-4 d-print-none">
                <div class="float-left">
                    <button type="button" class="btn btn-sm btn-warning" v-on:click="generateReport()" :disabled="generating">
                        <template v-if="generating"><i class="fa fa-sync fa-spin"></i> Generating</template>
                        <template v-else><i class="fa fa-list"></i> Generate</template>
                    </button>
                </div>
                <div class="float-left ml-2">
                    <button type="button" class="btn btn-sm btn-default" v-on:click="generateReport(true)" :disabled="generating">
                        <i class="fa fa-file-alt"></i> Export
                    </button>
                </div>
                <div class="float-left ml-2">
                    <b-form-radio-group v-model="export_type" name="export_type" plain>
                        <b-form-radio value="xlsx">
                            <i class="fa fa-file-excel"></i> XLSX
                        </b-form-radio>
                        <b-form-radio value="pdf">
                            <i class="fa fa-file-pdf"></i> PDF
                        </b-form-radio>
                    </b-form-radio-group>
                </div>
                <div class="float-left">
                    <b-form-checkbox v-model="export_url" :value="true" :unchecked-value="false" plain>
                        Add Export to URL
                    </b-form-checkbox>
                </div>
                <div class="clearfix"></div>
            </div>

            <div class="card mt-2" v-if="report_has_run">
                <div class="card-header d-print-none">
                    <strong>Results ({{ total }})</strong>
                </div>
                <div class="card-body" style="overflow-x: auto;">
                    <div class="alert alert-warning" v-if="total && queued == true">
                        Browser Results are limited to 4,000 records. The total for your search criteria match {{ total }} records. Amos is building your report (XLSX) and will email you the results. 
                    </div>
                    <table class="table table-bordered small" v-if="results.length > 0">
                        <thead class="thead-light">
                            <tr>
                                <th v-for="field in resultFields">{{ field.label }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in results">
                                <td v-for="field in resultFields">
                                    <span v-if="field.key === 'tax_id'">{{ row[field.key] | taxId }}</span>
                                    <span v-else-if="field.key === 'date_of_birth' && row[field.key] !== null">{{ row[field.key] | moment('MM/DD/YYYY') }}</span>
                                    <span v-else-if="field.key === 'marketer_date_assigned' && row[field.key] !== null">{{ row[field.key] | moment('MM/DD/YYYY') }}</span>
                                    <span v-else>{{ row[field.key] }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    //Field-key lookup sets. Defined once at module load so the row conditionals
    //(hasProduct, hasAddressType, etc.) don't rebuild an array on every re-render.
    const HAS_ACTIVE = new Set([
        'agents.is_active',
        'products.is_active',
    ]);

    const HAS_LOGIC = new Set([
        //Agent Fields
        'agents.last_name',
        'agents.first_name',
        'agents.middle_name',
        'agents.suffix',
        'agents.nickname',
        'record_addresses.city',
        'agents.res_license_nbr',
        'manager_name',
        'agents.tax_id',
        'agents.ssn',
        'agents.npn',

        //Agency Fields
        'agencies.agency_name',
        'agencies.npn',
        'agencies.resident_license_nbr',
        'agencies.tax_id',

        //Products
        'products.product_name',
        'products.product_desc',
        'products.website',
    ]);

    const HAS_DATE_LOGIC = new Set([
        'agents.date_of_birth',
        'agents.eo_expiration',
        'agents.ahip',
        'agents.updated_at',
        'seller_agency.updated_at',
        'product_contracts.updated_at',

        //Marketer
        'agents.marketer_date_assigned',
        'agents.created_at',
        'agencies.marketer_date_assigned',
        'agencies.created_at',

        //Product Stuff
        'products.product_expires_at',
        'products.created_at',
        'products.updated_at',
    ]);

    const HAS_USER_LOGIC = new Set([
        'agents.last_user_id',
        'seller_agency.last_user_id',
        'product_contracts.last_user_id',
    ]);

    const HAS_PRODUCT = new Set([
        'manager_name',
        'product_contracts.advance',
        'product_contracts.commission',
        'product_levels.commission',
        'product_contracts.writing_nbr',
        'product_contracts.requested_date',
        'product_contracts.sent_date',
        'product_contracts.credentialing_date',
        'product_contracts.certification_date',
        'product_contracts.termination_date',
        'product_contracts.product_id',
        'product_contracts.product_id_not_sold',
        'product_contracts.product_id_inactive',
        'product_contracts.product_id_active',
        'product_contracts.product_id_manager',
        'product_contracts.notes',
        'products.product_name',
        'product_levels.product_level_name',
        'product_contract_statuses.contract_status_name',
        'product_contract_statuses.contract_status_type',
        //'product_contracts.contract_status_id',
    ]);

    const HAS_PRODUCT_TYPE = new Set([
        'products.product_type_id',
        'products.product_type_id_manager',
    ]);

    const HAS_ADDRESS_TYPE = new Set([
        'record_addresses.address',
        'record_addresses.city',
        'record_addresses.state_id',
        'record_addresses.zip',
        'states.state_name',
    ]);

    const HAS_AGENCY = new Set([
        'product_contracts.manager_id_agency',
        'agents.direct_manager_id_agency',
        'agents.top_manager_id_agency',
        'agencies.direct_manager_id_agency',
        'agencies.top_manager_id_agency',
    ]);

    const HAS_AGENT = new Set([
        'product_contracts.manager_id_agent',
        'agents.marketer_id',
        'agencies.marketer_id',
        'agents.direct_manager_id_agent',
        'agents.top_manager_id_agent',
        'agencies.direct_manager_id_agent',
        'agencies.top_manager_id_agent',
    ]);

    const HAS_OKAY_TO_CONTACT = new Set([
        'agents.okay_to_contact',
        'agents.okay_to_contact_downline',
        'agencies.okay_to_contact',
        'agencies.okay_to_contact_downline',
    ]);

    export default {
        data() {
            return {
                loading: true,
                generating: false,
                queued: false,
                report_has_run: false,
                can_edit: false,
                save_as_new: false,
                show_display_fields: true,
                export_type: 'xlsx',
                export_url: false,
                id: this.$route.params.id,
                tz: this.$root.timezone,
                record_type: 'Agent',
                reportTypeOptions: [
                    {value: 'Agent', text: 'Agent'},
                    {value: 'Agency', text: 'Agency'},
                    {value: 'Product', text: 'Product'}
                    // {value: 'Client', text: 'Client'},
                    // {value: 'Carrier', text: 'Carrier'},
                ],
                display_fields: {},
                search_fields: {},
                selected_display_fields: [
                    {
                        key: 'agents.last_name',
                        value: null,
                    }
                ],
                // selected_search_fields: [
                //     {
                //         key: 'agents.last_name',
                //         logic: 'LIKE',
                //         value: null,
                //     }
                // ],
                selected_search_fields: [],
                states: [],
                products: [],
                agencies: [],
                users: [],
                results: [],
                total: null,
                manager_agents: [],
                do_not_contact_codes: [],
                marketer_codes: [],
                contract_statuses: [],
                product_types: [],
                report: {},
                errors: {},

                phoneTypes: [
                    {value: null, text: '-- Select Type --'},
                    {value: 'Primary', text: 'Primary'},
                    {value: 'Residence', text: 'Residence'},
                    {value: 'Business', text: 'Business'},
                    {value: 'Cell', text: 'Cell'},
                    {value: 'Fax', text: 'Fax'},
                ],
            }
        },

        watch: {
            record_type(newVal, oldVal){
                //Check for Report ID
                if(this.report && this.report.id)
                {
                    return;
                }

                //Check if there are query params and the setUrlParams is being called
                if(this.$route.query.record_type && this.$route.query.record_type == newVal)
                {
                    return;
                }

                //Reset the Fields
                switch(newVal)
                {
                    case 'Agent':
                        //SEt it
                        this.selected_display_fields = [
                            {
                                key: 'agents.last_name',
                                value: null,
                            }
                        ];

                        this.selected_search_fields = [
                            {
                                key: 'agents.last_name',
                                logic: 'LIKE',
                                value: null,
                            }
                        ];
                        break;

                    case 'Agency':
                        //SEt it
                        this.selected_display_fields = [
                            {
                                key: 'agencies.agency_name',
                                value: null,
                            }
                        ];

                        this.selected_search_fields = [
                            {
                                key: 'agencies.agency_name',
                                logic: 'LIKE',
                                value: null,
                            }
                        ];
                        break;

                    case 'Client':

                        break;

                    case 'Carrier':

                        break;

                    case 'Product':
                        this.selected_display_fields = [
                            {
                                key: 'products.product_name',
                                value: null,
                            }
                        ];

                        this.selected_search_fields = [
                            {
                                key: 'products.product_name',
                                logic: 'LIKE',
                                value: null,
                            }
                        ];
                        break;
                }
            },

            export_url(new_val, old_val){
                //RUn the helper
                this.updateUrlParams();
            },
        },

        computed: {
            canEdit(){
                //Check The ID 
                if( this.report 
                    && this.report.id 
                    && this.report.user_id == this.$store.state.user.id )
                {
                    return true;
                }

                //Default to permission
                return this.$root.hasPermission("reports.manage.all");
            },

            hasUnsavedChanges(){
                //Only meaningful once a saved report is loaded
                if(!this.report || !this.report.id)
                {
                    return false;
                }

                //Compare the current selections against what's stored on the report.
                //Uses a canonical, order-stable serialization so re-generating an
                //unchanged report (which still writes params to the URL) is not flagged.
                var savedDisplay = this.parseSavedFields(this.report.display_fields);
                var savedSearch = this.parseSavedFields(this.report.search_fields);

                return this.normalizeFields(this.selected_display_fields) !== this.normalizeFields(savedDisplay)
                    || this.normalizeFields(this.selected_search_fields) !== this.normalizeFields(savedSearch);
            },

            agencyOptions(){
                //Setup
                var options = [{value: null, text: '-- Select an Agency --'}];

                //Main Loop
                for(var i = 0; i < this.agencies.length; i++)
                {
                    options.push({value: this.agencies[i].id, text: this.agencies[i].agency_name});
                }

                //Send it
                return options;
            },

            stateOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a State --'}];

                //Main Loop
                for(var i = 0; i < this.states.length; i++)
                {
                    options.push({value: this.states[i].id, text: this.states[i].state_name});
                }

                //Send it
                return options;
            },

            doNotContactOptions(){
                //Setup
                var options = [{value: null, text: '-- Select Reason for Do Not Contact --'}];

                //Main Loop
                for(var i = 0; i < this.do_not_contact_codes.length; i++)
                {
                    options.push({value: this.do_not_contact_codes[i].id, text: this.do_not_contact_codes[i].dnc_code_reason});
                }

                //Send it
                return options;
            },

            marketerCodeOptions(){
                //Setup
                var options = [{value: null, text: '-- Select Markter Relationship Code --'}];

                //Main Loop
                for(var i = 0; i < this.marketer_codes.length; i++)
                {
                    options.push({value: this.marketer_codes[i].id, text: this.marketer_codes[i].marketer_code_label});
                }

                //Send it
                return options;
            },

            contractStatusOptions(){
                //Setup
                var options = [{value: null, text: '-- Select Contract Status --'}];

                //Main Loop
                for(var i = 0; i < this.contract_statuses.length; i++)
                {
                    options.push({value: this.contract_statuses[i].id, text: this.contract_statuses[i].contract_status_name});
                }

                //Send it
                return options;
            },

            contractStatusTypeOptions(){
                //Setup
                var options = [{value: null, text: '-- Select Contract Status Type --'}];

                //Setup Unique
                var types = [];

                //Main Loop
                for(var i = 0; i < this.contract_statuses.length; i++)
                {
                    //Check If Exists
                    if(types.indexOf(this.contract_statuses[i].contract_status_type) === -1)
                    {
                        options.push({value: this.contract_statuses[i].contract_status_type, text: this.contract_statuses[i].contract_status_type});
                    }

                    //Append
                    types.push(this.contract_statuses[i].contract_status_type);
                }

                //Send it
                return options;
            },

            productOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a Product --'}];

                //Main Loop
                for(var i = 0; i < this.products.length; i++)
                {
                    options.push({value: this.products[i].id, text: this.products[i].product_name});
                }

                //Send it
                return options;
            },

            productTypeOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a Product Type --'}];

                //Main Loop
                for(var i = 0; i < this.product_types.length; i++)
                {
                    options.push({value: this.product_types[i].id, text: this.product_types[i].product_type_name});
                }

                //Send it
                return options;
            },

            userOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a User --'}];

                //Main Loop
                for(var i = 0; i < this.users.length; i++)
                {
                    options.push({value: this.users[i].id, text: (this.users[i].last_name + ', ' + this.users[i].first_name)});
                }

                //Send it
                return options;
            },

            logicOptions(){
                //Send it
                return [
                    {value: null, text: '-- Select Logic --'},
                    {value: 'LIKE', text: 'is like'},
                    {value: '=', text: 'is exactly'},
                ];
            },

            dateLogicOptions(){
                //Send it
                return [
                    {value: null, text: '-- Select Logic --'},
                    {value: '<', text: 'before'},
                    {value: '=', text: 'exactly'},
                    {value: '>', text: 'after'},
                ];
            },

            displayFieldOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a Display Field --'}];

                //Check for Empty
                if( (Object.keys(this.display_fields)).length === 0)
                {
                    //Stop Here
                    return options;
                }

                //Loop and Build
                var keys = Object.keys(this.display_fields[this.record_type]);

                //Loop
                for (var i = 0; i < keys.length; i++)
                {
                    options.push({value: keys[i], text: this.display_fields[this.record_type][keys[i]]});
                }

                //Send it
                return options;
            },

            searchFieldOptions(){
                //Setup
                var options = [{value: null, text: '-- Select a Search Field --'}];

                //Check for Empty
                if( (Object.keys(this.search_fields)).length === 0)
                {
                    //Stop Here
                    return options;
                }

                //Loop and Build
                var keys = Object.keys(this.search_fields[this.record_type]);

                //Loop
                for (var i = 0; i < keys.length; i++)
                {
                    options.push({value: keys[i], text: this.search_fields[this.record_type][keys[i]]});
                }

                //Send it
                return options;
            },

            resultFields(){
                //Build
                var fields = [];

                for (var i = 0; i < this.selected_display_fields.length; i++)
                {
                    //Check for NULL
                    if(this.selected_display_fields[i].key == null)
                    {
                        continue;
                    }

                    //Split out the table
                    var name_parts = this.selected_display_fields[i].key.split('.');

                    //Setup
                    var label = this.display_fields[this.record_type][this.selected_display_fields[i].key];
                    var key = name_parts.length == 2 ? name_parts[1] : name_parts[0];

                    //Switch to do some custom stuff
                    switch(this.selected_display_fields[i].key)
                    {
                        case 'manager_name':
                        case 'product_contracts.commission':
                        case 'product_levels.commission':
                        case 'product_contracts.advance':
                        case 'product_contracts.writing_nbr':
                        case 'product_contracts.requested_date':
                        case 'product_contracts.sent_date':
                        case 'product_contracts.credentialing_date':
                        case 'product_contracts.certification_date':
                        case 'product_contracts.termination_date':
                        case 'product_contracts.product_id':
                        case 'product_contracts.product_id_not_sold':
                        case 'product_contracts.product_id_inactive':
                        case 'product_contracts.product_id_active':
                        case 'product_contracts.product_id_manager':
                        case 'product_contracts.notes':
                        case 'product_contract_statuses.contract_status_name':
                        case 'product_contract_statuses.contract_status_type':
                            //Main Loop
                            for(var j = 0; j < this.products.length; j++)
                            {
                                //Check ID
                                if(this.selected_display_fields[i].value == this.products[j].id)
                                {
                                    //Append the Product Name
                                    label = label.replace('the Product', this.products[j].product_name);

                                    //Set the Key
                                    key = label;

                                    //Do Some Custom Stuff
                                    if(key.includes('Writing#'))
                                    {
                                        key = key.replace('Writing#', 'Writing Nbr');
                                    }

                                    //Do Some other Things
                                    if(key.includes(' (Legacy Field)'))
                                    {
                                        key = key.replace(' (Legacy Field)', '');
                                    }

                                    //Custom Stuff for Status
                                    if(key === 'Contract Status Name')
                                    {
                                        //Set to include project
                                        key = key + ' for ' + this.products[j].product_name;

                                        //Set the label
                                        label = label + ' for ' + this.products[j].product_name;
                                    }

                                    //Custom Stuff for Status
                                    if(key === 'Contract Status Type')
                                    {
                                        //Set to include project
                                        key = key + ' for ' + this.products[j].product_name;

                                        //Set the label
                                        label = label + ' for ' + this.products[j].product_name;
                                    }
                                }
                            }
                            break;

                        case 'record_addresses.address':
                        case 'record_addresses.city':
                        case 'record_addresses.zip':
                            //Add the Field Type
                            label = this.selected_display_fields[i].type+' '+this.$root.capitalizeFirstLetter(key);
                            key = label;
                            break;

                        case 'states.state_name':
                            //Add the State
                            label = this.selected_display_fields[i].type+' State';
                            key = label;
                            break;

                        case 'record_phones.phone':
                            //Add the State
                            label = this.selected_display_fields[i].type+' Phone';
                            key = label;
                            break;
                    }

                    //Push
                    fields.push({
                        key: key,
                        label: label,
                        sortable: true,
                    });
                }

                //Send i
                return fields;
            },

            addressTypes(){
                //Should look like this: 
                // [
                //     {value: 'Primary', text: 'Primary'},
                //     {value: 'Residence', text: 'Residence'},
                //     {value: 'Business', text: 'Business'},
                //     {value: 'Mailing', text: 'Mailing'},
                //     {value: 'PO Box', text: 'PO Box'},
                // ]

                //Setup — lead with a null placeholder (like every other select) so a
                //freshly added field starts matched to it. Without this, the model stays
                //undefined, the browser shows the first real option, and selecting that
                //option fires no change event, so .type is never committed or persisted.
                var options = [{value: null, text: '-- Select Type --'}];

                //Setup
                var types = this.$store.state.constants.addressTypes;

                //Loop and Push
                for (let index = 0; index < types.length; index++) 
                {
                    options.push({value: types[index], text: types[index]});
                }

                //Send it
                return options;
            },
        },

        methods: {
            //Get the Customer
            getBuilder(){
                //Send it
                this.$http.get('/reports/custom' + (this.id ? '/'+this.id : ''))
                    .then(response => {
                        //Reset
                        this.loading = false;

                        //Set the user
                        this.display_fields = response.data.display_fields;
                        this.search_fields = response.data.search_fields;
                        this.states = response.data.states;
                        this.products = response.data.products;
                        this.agencies = response.data.agencies;
                        this.users = response.data.users;
                        this.can_edit = response.data.can_edit;

                        //Other Resources for Various Search Filters
                        this.do_not_contact_codes = response.data.do_not_contact_codes;
                        this.marketer_codes = response.data.marketer_codes;
                        this.contract_statuses = response.data.contract_statuses;
                        this.product_types = response.data.product_types;

                        //Set params from URL
                        this.setParamsFromUrl();

                        //A saved report owns its own field definitions and generation.
                        //Only fall back to the URL-params path for the ad-hoc builder,
                        //otherwise both paths fire and generateReport() runs twice.
                        if(response.data.report)
                        {
                            //Set the repor
                            this.report = response.data.report;
                            //this.show_display_fields = false;

                            //setup the Report SEttings
                            var settings = JSON.parse(this.report.filter_fields);

                            //A URL only carries display_/search_ params because the user
                            //edited fields and generated, so its presence means there are
                            //unsaved changes. When present, keep what setParamsFromUrl()
                            //already restored instead of reverting to the saved definition.
                            var hasUrlFields = Object.keys(this.$route.query).some(function(key){
                                return key.indexOf('display_') === 0 || key.indexOf('search_') === 0;
                            });

                            //record_type can't be edited on a saved report; take it from
                            //the saved settings unless the URL already supplied it.
                            if(!this.$route.query.record_type)
                            {
                                this.record_type = settings.record_type;
                            }

                            //Only load the saved field definitions when the URL has no
                            //field params (i.e. there are no unsaved edits to preserve).
                            if(!hasUrlFields)
                            {
                                //Set the fields
                                this.selected_display_fields = JSON.parse(this.report.display_fields);

                                //Check for Empty
                                if(this.report.search_fields)
                                {
                                    //Setup
                                    this.selected_search_fields = JSON.parse(this.report.search_fields);
                                }
                            }

                            //Check for Export TYpe
                            if(settings.export_type && !this.$route.query.export_type)
                            {
                                this.export_type = settings.export_type;
                            }

                            //Auto-Generate
                            this.generateReport();
                        }
                        //Ad-hoc builder opened with shareable URL params
                        else if(Object.keys(this.$route.query).length > 0)
                        {
                           //Generate or Export it
                            this.generateReport( this.export_url );
                        }
                    })
                    .catch(error => {
                        //Debugging
                        console.log(error);
                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                        this.generating = false;
                    });
            },

            //Get the Customer
            generateReport(do_export = false){
                //Loading
                this.generating = true;
                this.errors = {};
                this.total = null;

                //Setup
                var payload = {
                    export: do_export,
                    export_type: this.export_type,
                    record_type: this.record_type,
                    display_fields: this.selected_display_fields,
                    search_fields: this.selected_search_fields,
                    columns: this.resultFields,
                };

                //Send it
                this.$http.post('/reports/custom', payload)
                    .then(response => {
                        //Update
                        this.updateUrlParams();

                        //We can always set the total
                        this.total = response.data.total;
                        this.queued = response.data.queued;

                        //Set the flag
                        this.report_has_run = true;

                        //Check for Queued
                        if(response.data.queued === true)
                        {
                            //Alert the User
                            this.$toastr.s(response.data.message);                            

                            //Stop Here
                            return;
                        }

                        //Check for Export
                        if(do_export)
                        {
                            //Open the new window to Download the File
                            if(response.data.queued)
                            {
                                //Alert the User
                                this.$toastr.s(response.data.message);
                            }
                            else
                            {
                                //Open the download URL
                                window.open(response.data.url);
                            }
                        }
                        else
                        {
                            //Set the results
                            this.results = response.data.results;
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data.errors;

                            //Alert
                            this.$toastr.e('Validation Errors Occurred');
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.e(error.response.data, 'Failed to Generate Report');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.generating = false;
                    });
            },

            //Get the Customer
            saveReport(){
                //Loading
                this.generating = true;
                this.errors = {};

                //Append the Fields
                this.report.display_fields = this.selected_display_fields;
                this.report.search_fields = this.selected_search_fields;
                this.report.report_type = 'Custom';
                this.report.filter_fields = {
                    record_type: this.record_type,
                    columns: this.resultFields,
                    export_type: this.export_type,
                };
                this.report.save_as_new = this.save_as_new;

                //Send it
                this.$http.post('/reports/save', this.report)
                    .then(response => {
                        //Set it
                        this.report = response.data;

                        //Alert
                        this.$toastr.s('Report Saved');
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data.errors;

                            //Alert
                            this.$toastr.e('Validation Errors Occurred');
                        }
                        else
                        {
                            //Alert the user
                            this.$toastr.e(error.response.data, 'Failed to Generate Report');
                        }
                    })
                    .then(() => {
                        //Reset
                        this.generating = false;
                    });
            },

            //Parses a saved report's field definition, which may arrive as a JSON
            //string (loaded from the DB) or an already-decoded array (after a save).
            parseSavedFields(raw){
                //Already an array
                if(Array.isArray(raw))
                {
                    return raw;
                }

                //JSON string
                if(typeof raw === 'string' && raw.length)
                {
                    try
                    {
                        return JSON.parse(raw);
                    }
                    catch(e)
                    {
                        return [];
                    }
                }

                //Nothing usable
                return [];
            },

            //Serializes a set of fields into a canonical, order-stable string so two
            //field sets can be compared for equality regardless of key order or whether
            //values arrived as URL strings vs saved numbers.
            normalizeFields(fields){
                return JSON.stringify((fields || []).map(function(field){
                    //Guard
                    field = field || {};

                    //Build a normalized shape with a fixed key order
                    var normalized = {};

                    //Only the properties that define a field
                    ['key', 'value', 'logic', 'type', 'contract_status_id'].forEach(function(prop){
                        //Grab the value
                        var val = field[prop];

                        //Treat null/undefined/'' the same, and coerce everything else to
                        //a string so '5' (from the URL) matches 5 (from saved JSON).
                        normalized[prop] = (val === undefined || val === null || val === '') ? null : String(val);
                    });

                    //Send it
                    return normalized;
                }));
            },

            addDisplayField(){
                //Push it
                this.selected_display_fields.push({key: null, value: null});
            },

            removeDisplayField(i){
                //SPlice
                this.selected_display_fields.splice(i, 1);
            },

            addSearchField(){
                //Push it
                this.selected_search_fields.push({
                    key: null,
                    logic: null,
                    value :null,
                    type: null,
                });
            },

            removeSearchField(i){
                //SPlice
                this.selected_search_fields.splice(i, 1);
            },

            hasActive(key){
                return HAS_ACTIVE.has(key);
            },

            hasLogic(key){
                return HAS_LOGIC.has(key);
            },

            hasDateLogic(key){
                return HAS_DATE_LOGIC.has(key);
            },

            hasUserLogic(key){
                return HAS_USER_LOGIC.has(key);
            },

            hasProduct(key){
                return HAS_PRODUCT.has(key);
            },

            hasProductType(key){
                return HAS_PRODUCT_TYPE.has(key);
            },

            hasAddressType(key){
                return HAS_ADDRESS_TYPE.has(key);
            },

            hasAgency(key){
                return HAS_AGENCY.has(key);
            },

            hasAgent(key){
                return HAS_AGENT.has(key);
            },

            hasOkayToContact(key){
                return HAS_OKAY_TO_CONTACT.has(key);
            },

            openPrint(){
                //Open it
                window.print();
            },

            searchAgents(search, loading){
                //Minimum 3 characters
                if(search.length < 3)
                {
                    //Display an Alert?

                    //STop
                    return;
                }

                //Must call this to let VueSelect know what we are doing
                loading(true);

                //Build the Search Payload
                var payload = {
                    query: search,
                    limit: 20,
                    ascending: 1,
                    page: 1,
                    byColumn: 0,
                    orderBy: 'agents.last_name',
                    typeahead: true,
                };

                //Show a Loading INdicator?
                this.$http.get('/agents', payload)
                    .then(response => {
                        // //Set the values
                        this['manager_agents'] = response.data.data;
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .then(() => {
                        //REset
                        loading(false);
                    });
            },

            updateUrlParams(){
                //Setup
                var params = {
                    record_type: this.record_type,
                };

                //Loop
                for (var i = 0; i < this.selected_display_fields.length; i++)
                {
                    //Append it
                    params['display_key_'+i] = this.selected_display_fields[i].key;

                    //Check for a value
                    if(this.selected_display_fields[i].value)
                    {
                        params['display_value_'+i] = this.selected_display_fields[i].value;
                    }

                    //Check for a type (address/phone type)
                    if(this.selected_display_fields[i].type)
                    {
                        params['display_type_'+i] = this.selected_display_fields[i].type;
                    }
                }

                //Loop
                for (var i = 0; i < this.selected_search_fields.length; i++)
                {
                    //Append it
                    params['search_key_'+i] = this.selected_search_fields[i].key;

                    //Check for a value
                    if(this.selected_search_fields[i].logic)
                    {
                        params['search_logic_'+i] = this.selected_search_fields[i].logic;
                    }

                    //Check for a value
                    if(this.selected_search_fields[i].value)
                    {
                        params['search_value_'+i] = this.selected_search_fields[i].value;
                    }

                    //Check for a type (address type)
                    if(this.selected_search_fields[i].type)
                    {
                        params['search_type_'+i] = this.selected_search_fields[i].type;
                    }

                    //Check for a value
                    if(this.selected_search_fields[i].contract_status_id)
                    {
                        params['search_contract_status_id_'+i] = this.selected_search_fields[i].contract_status_id;
                    }
                }

                //console.log(params);

                //Build the URL
                var url = window.location.origin + window.location.pathname + '?';

                //Loop and Build
                var keys = Object.keys(params);

                //Loop
                for (var i = 0; i < keys.length; i++)
                {
                    //Append
                    //url.searchParams.set(keys[i], params[keys[i]]);

                    //Append
                    url += (i > 0 ? '&' : '') + keys[i] + '=' + encodeURIComponent(params[keys[i]]);
                }

                //Check for Export Mode
                if(this.export_url === true)
                {
                    //Append the Export Flag
                    url += '&export_type=' + this.export_type;
                }

                //Push it
                //this.$router.push({path: this.$route.path, query: params });
                window.history.replaceState({}, '', url);
            },

            setParamsFromUrl(){
                //Get ALL the params
                var params = this.$route.query;

                //BUild the Record Type
                if(params.record_type)
                {
                    this.record_type = params.record_type;
                }

                //Set the Param
                if(params.export_type)
                {
                    //Set it
                    this.export_type = params.export_type;

                    //Set the helper
                    this.export_url = true;
                }

                //Build the Display Fields
                var display = [];
                var search = [];

                //Setup
                var keys = Object.keys(params);

                //console.log(params);

                //Loop
                for (var i = 0; i < keys.length; i++)
                {
                    //Skip Record Name
                    if(keys[i] == 'record_type')
                    {
                        continue;
                    }

                    //setup the index (parse the full trailing number, not just the last digit)
                    var indexMatch = keys[i].match(/_(\d+)$/);

                    //Skip any param that doesn't end in an index
                    if(indexMatch === null)
                    {
                        continue;
                    }

                    //Grab the numeric index
                    var index = parseInt(indexMatch[1]);

                    //Strip off the Identifier and the trailing Index
                    var attr = keys[i].replace(/_\d+$/, '');
                    attr = attr.replace('display_', '');
                    attr = attr.replace('search_', '');

                    //Check for a display:
                    if(keys[i].includes('display'))
                    {
                        //Build it
                        if(typeof display[index] == 'undefined')
                        {
                            display[index] = {
                                key: null,
                                value: null,
                            };
                        }

                        //NOW we can set what we need to
                        display[index][attr] = params[keys[i]];
                    }

                    //Check for a display:
                    if(keys[i].includes('search'))
                    {
                        //Build it
                        if(typeof search[index] == 'undefined')
                        {
                            search[index] = {
                                key: null,
                                logic: null,
                                value: null,
                            };
                        }

                        //NOW we can set what we need to
                        search[index][attr] = params[keys[i]];
                    }
                }

                //console.log(display);
                //console.log(search);

                //Set the Values
                if(display.length > 0)
                {
                    this.selected_display_fields = display;
                }

                //Set the Search
                if(search.length > 0)
                {
                    this.selected_search_fields = search;
                }
            }
        },

        mounted() {
            //Get the user
            this.getBuilder();
        }
    }
</script>
